import { Button } from '@talentmesh/ui/components/button';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { ShortlistedProfile, ShortlistedStatus } from '../../api/DTO/ShortlistedProfile';
import useAISearchCounters from '../../api/useAISearchCounters';
import { useLinkedInMessageOutreach } from '../../api/useOutreachCandidate';
import { useCachedProfileContactInfos } from '../../api/useProfileContactInfo';
import { useDeleteShortlistedProfiles, useUpdateShortlistedProfilesStatus } from '../../api/useShortlistedProfiles';
import htmlToPlainText from '../../utils/htmlToPlainText';
import { SelectedProfile } from '../AiSearchProfilePopout/AiSearchProfilePopoutContext';
import ShortlistedStatusAction from './components/ShortlistedStatusAction';
import { useOutreachCandidatePopoutContext } from './Outreach/OutreachContext';
import { useShortlistedListContext } from './utils/ShortlistedListContext';
import { useShortlistedTableContext } from './utils/ShortlistedTableContext';
import UIStrings from '@/Utils/UIStrings';

export type SelectedShortlistedProfile = SelectedProfile & Partial<Pick<ShortlistedProfile, 'emailSentCount'>>;

interface ShortlistedButtonActionsProps {
    selectedRows: SelectedShortlistedProfile[];
    /**
     * this option will:
     * - hide the "Send email" button
     * - auto close when user selects special status (e.g. ContactTaskInMail)
     */
    inDialog?: boolean;
    closeDialogCallback?: () => void;
}

const ShortlistedButtonActions = ({ selectedRows, inDialog, closeDialogCallback }: ShortlistedButtonActionsProps) => {
    const { mutate: mutateShortlistedProfiles, isLoading: isShortlistedProfilesLoading } = useShortlistedListContext();
    const { trigger: triggerStatusChange, isMutating: isUpdateShortlistedProfilesStatusMutating } =
        useUpdateShortlistedProfilesStatus();
    const { trigger: triggerProfileDelete, isMutating: isDeleteShortlistedProfilesMutating } =
        useDeleteShortlistedProfiles();
    const { mutate: mutateAISearchCounters, isLoading: isAISearchCountersLoading } = useAISearchCounters();
    const { resetRowSelection } = useShortlistedTableContext();
    const { openSendEmailPopout, openLinkedInMessagePopout } = useOutreachCandidatePopoutContext();
    const contactInfos = useCachedProfileContactInfos(selectedRows.map(({ url }) => url));
    const { data: linkedInMessage } = useLinkedInMessageOutreach();
    const canSomeSendEmail = useMemo(() => {
        const profilesWithAvailableEmail = contactInfos.filter((contact) =>
            contact.info ? contact.info.personalEmailAvailable : false,
        );

        const anyAvailableEmail = !!profilesWithAvailableEmail.length;
        const anyNotContacted = selectedRows
            .filter(({ url }) => profilesWithAvailableEmail.find((profile) => url === profile.url))
            .some(({ emailSentCount }) => !emailSentCount);

        return anyAvailableEmail && anyNotContacted;
    }, [contactInfos]);
    const anyProfileSentEmail = useMemo(
        () => selectedRows.some(({ emailSentCount }) => !!emailSentCount),
        [selectedRows],
    );

    const handleOpenSendEmail = () => openSendEmailPopout(selectedRows);
    const handleOpenLinkedInMessage = () => openLinkedInMessagePopout(selectedRows);

    const handleStatusChange = async (newStatus: ShortlistedStatus) => {
        if (newStatus === 'ContactTaskInMail') {
            if (!linkedInMessage) {
                handleOpenLinkedInMessage();
                if (inDialog) {
                    closeDialogCallback?.();
                }
                return;
            }

            const plainText = htmlToPlainText(linkedInMessage.message || '');

            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([linkedInMessage.message], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
            });

            navigator.clipboard.write([clipboardItem]);
        }

        await triggerStatusChange({
            shortlistedProfilesIds: selectedRows.map(({ id }) => id),
            shortlistedStatus: newStatus,
        });
        toast.success(
            `${
                newStatus === 'ContactTaskInMail' ? `${UIStrings.MessageCopiedToClipboard}. ` : ''
            }${UIStrings.NumberOfCandidateStatus(selectedRows.length, UIStrings[newStatus])}`,
        );
        mutateShortlistedProfiles();
        resetRowSelection(true);
    };

    const handleProfileDelete = async () => {
        await triggerProfileDelete({ shortlistedProfilesIds: selectedRows.map(({ id }) => id) });
        toast.success(UIStrings.NumberOfCandidateStatus(selectedRows.length, UIStrings.Removed));

        mutateShortlistedProfiles();
        mutateAISearchCounters();
        resetRowSelection(true);

        closeDialogCallback?.();
    };

    const isLoading = isShortlistedProfilesLoading || isAISearchCountersLoading;
    const isStatusChangeOngoing = isUpdateShortlistedProfilesStatusMutating || isLoading;
    const isProfileDeleteOngoing = isDeleteShortlistedProfilesMutating || isLoading;

    return (
        <div className="flex items-center gap-2">
            <ShortlistedStatusAction onChangeCallback={handleStatusChange} />
            {!inDialog && (
                <>
                    <Button
                        size="sm"
                        variant="default"
                        onClick={handleOpenLinkedInMessage}
                        className="whitespace-nowrap"
                    >
                        {UIStrings.SendLinkedInInMail}
                    </Button>
                    <span
                        title={
                            !canSomeSendEmail
                                ? UIStrings.NoSelectedCandidatesHaveAnEmailAddressOrHaventBeenContacted
                                : undefined
                        }
                    >
                        <Button
                            size="sm"
                            variant="default"
                            onClick={handleOpenSendEmail}
                            disabled={!canSomeSendEmail}
                            className="whitespace-nowrap"
                        >
                            {UIStrings.SendEmail}
                        </Button>
                    </span>
                </>
            )}
            <span
                title={
                    anyProfileSentEmail ? UIStrings.SomeSelectedCandidatesHaveBeenContactedAndCantBeRemoved : undefined
                }
            >
                <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleProfileDelete}
                    disabled={anyProfileSentEmail || isStatusChangeOngoing}
                >
                    {isProfileDeleteOngoing && <Spinner className="mr-1 h-3 w-3" />}
                    {UIStrings.Remove}
                </Button>
            </span>
        </div>
    );
};

export default ShortlistedButtonActions;
